import { ref, reactive, computed } from 'vue';
import { defineStore } from 'pinia';
import { useToast } from 'primevue/usetoast';
import { normalizeAvailabilityKey, buildMonthKey } from '../utils/availability';
import { useServices } from '../api/services';
import { apiClient } from '../api/axiosInstance';
import { useFileCompression } from '../upload/useFileCompression';
import { useTenantStore } from './useTenantStore';
import { DEFAULT_TIMEZONE } from '../utils/date';
import type { AppointmentComment } from '../api/services/adapters/comment';
import type { UploadProgressEvent } from '../api/services/interfaces/JobService';
import { useInvoicesStore, type ReportStatus } from './useInvoicesStore';

export interface Address {
  id: number;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip_code: string;
  time_zone: string;
  name?: string;
  invalid?: boolean;
  primary?: boolean;
  /** Geocoded coordinates (wire `address.coords`); null when not geocoded.
   * Centers the technician-tracking map on the service address (TRACK-01). */
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * Per-tech entry on the canonical multi-tech array. Each tech carries an
 * independent lifecycle (`technician_status`) — the hospitality card +
 * tracking map render off these, not the appointment-level status.
 *
 * Wire shape per `appointments[].technicians[]` / `appointment_technicians[]`
 * (v2) and per `appointment.technician` (v1, singular — back-filled into a
 * one-element array by the adapter). See CONTEXT D-14 / D-15.
 *
 * `route_color` is not standardised on the wire — the adapter assigns a
 * deterministic mapping by id when the backend doesn't ship it. See
 * RESEARCH §3.
 */
export interface AppointmentTechnician {
  /** The technician PROFILE id (`technician_profile.id`) — the wire roster key. */
  id: number;
  /**
   * The technician's USER id (`technician_profile.user_id`). Distinct from `id`.
   * The live GPS feed keys frames by `technicianId`, which the server sets to
   * the technician's USER id (`process_gps_update` merges `technicianId =>
   * user.id`), so route-color reconciliation for the live map matches a frame's
   * `technicianId` against this field, NOT `id` (Open Q3 / Pitfall 5).
   */
  user_id?: number;
  name: string;
  role?: string;
  avatar_url?: string;
  phone?: string;
  technician_status:
    | 'scheduled'
    | 'assigned'
    | 'accepted'
    | 'traveling'
    | 'arrived'
    | 'departed';
  arrived_at?: string;
  departed_at?: string;
  eta_minutes?: number;
  distance_miles?: number;
  route_color?: string;
}

/**
 * Customer-visible video tile on the appointment surface. Parallel to
 * `CustomerPhoto` / `CustomerDocument`. Read-only in Phase 4.6; the
 * upload affordance lands on a separate roadmap entry. See CONTEXT D-32.
 */
export interface CustomerVideo {
  id: number;
  url: string;
  thumbUrl: string;
  duration_seconds?: number;
  customer_visible: boolean;
  createdAt?: string;
  createdByName?: string;
  /**
   * Free-text media caption (PODZ-1830), tech-authored. Absent until backend
   * PODZ-1823 deploys the caption column — render defensively (nothing when
   * undefined).
   */
  caption?: string;
  /**
   * Upload source enum (PODZ-1830), threaded verbatim from the video wire.
   * The upload-meta footer resolves it to a human label via sourceLabel();
   * absent/unknown source omits the footer segment. Mirrors CustomerPhoto.source.
   */
  source?: string;
}

/**
 * Customer-visible note on the appointment surface. Research-gated per
 * CONTEXT D-35 — legacy customer portal did not surface notes on the
 * detail view. The type ships so adapter + downstream UI can light up
 * the moment the backend emits `customer_visible` notes; until then,
 * the adapter outputs an empty array and the card renders nothing.
 */
export interface CustomerNote {
  id: number;
  comment: string;
  customer_visible?: boolean;
  pinned?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Appointment {
  id: number;
  job_id: number;
  /**
   * Owning customer id. Customer-visible notes are polymorphic Comments on
   * the User (commentable_type=User), so the notes fetch scopes by this id,
   * not by the appointment or the job. Optional: older wires omit `customer`.
   */
  customer_id?: number;
  /**
   * The customer's AnyCable bearer token, served on the v1 appointment payload
   * (magic-link appointment_viewer scope as well as the authed scope). Drives
   * live technician tracking on BOTH the authenticated and the shared/magic-link
   * surfaces (PODZ-1821). Read locally by TrackingMapCard and intentionally NOT
   * persisted to the auth store — D1 magic-link isolation keeps the magic-link
   * token out of the global authenticated session.
   */
  customer_cable_token?: string;
  status: string;
  service_category: { id: number; name: string };
  address: Address;
  // 260524-lo7 (G6): widened to include optional `phone`. The v1 jbuilder
  // emits technician.phone for appointment-scope auth — the legacy consumer
  // portal reads it on the Contact Tech menu. The magic-link Contact Tech
  // pill (ContactTechCard.vue) consumes this field directly.
  technician?: { name: string; phone?: string };
  /**
   * Canonical multi-tech array (CONTEXT D-14). When the wire emits only
   * the singular `technician` field, the adapter back-fills this to a
   * one-element array — downstream rendering always reads off
   * `technicians[]`.
   */
  technicians?: AppointmentTechnician[];
  /**
   * Customer-visible team notes. Research-gated (CONTEXT D-35); shape
   * shipped now so the surface can activate the moment the backend
   * emits the field.
   */
  notes?: CustomerNote[];
  arrival_window_start: string;
  arrival_window_end: string;
  arrival_time?: string;
  departure_time?: string;
  scheduled_time: string;
  /**
   * Dispatch timestamp used as the gate for the late-fee strip (CONTEXT
   * D-11). v2 wire emits `dispatch_time`; v1 wire emits the typo
   * `iso_dispatch_time`. Adapter normalises both into this single field.
   */
  dispatch_time?: string;
  cancellation?: { reason: string; party: string };
  /**
   * Original v1 cancellation block — the `responsable` typo is preserved
   * verbatim because surfacing the misspelling is what the legacy API
   * emits and downstream consumers (legacy magic-link parity) read off
   * it directly. See CONTEXT D-36 / RESEARCH §1.5.
   */
  cancellation_details?: { reason: string; responsable: string };
  documents?: CustomerDocument[];
  photos?: CustomerPhoto[];
  custom_arrival_windows_enabled?: boolean;
  /**
   * Late-fee configuration emitted on the v1 appointment wire (see
   * /Users/chris/Sera/api/app/views/v1/appointments/_appointment.json.jbuilder
   * lines 27-35). Drives the late fee countdown timer in ArrivalDetails.vue.
   * Both fields are 0 when the department has late fees disabled.
   */
  settings?: { late_fee_per_minute_cents: number; max_late_fee_cents: number };
}

/**
 * Single line-item on a {@link Quote}. The minimal shape rendered by the
 * 4.6 quote modal — only what the wire's `quote.items` array carries on
 * the v2 jobs payload. Equipment links (per-item upsell URLs) are
 * preserved as opaque strings; the 4.6 modal does not render them yet
 * (Phase 5 quote work).
 */
export interface QuoteItem {
  id: number;
  description: string;
  quantity: number;
  unit_price_cents: number;
  subtotal_cents: number;
  equipment_links?: string[];
}

/**
 * Customer-facing quote tier embedded on `Job.quotes[]`. Legacy treats
 * `pending` and `denied` identically on the appointment surface (same
 * row, same CTA, same modal copy) per CONTEXT D-28; the card filter is
 * applied in the view, not here.
 *
 * HATEOAS: the Proceed CTA is gated on the presence of
 * `links.quote_proceed.href`. When the backend omits the link, the
 * button hides — never reconstruct the URL by convention.
 *
 * `payments_as_low_as_cents` + `jobless_financing` are wire-only in 4.6;
 * Phase 5 wires the financing flow.
 */
export interface Quote {
  id: number;
  quote_status: 'pending' | 'denied' | 'accepted' | 'expired';
  tier_label?: string;
  /**
   * Short customer-facing name for the tier, rendered next to the tier
   * label on the inline quote card (e.g. "Good · Standard furnace"). When
   * absent, the row renders just the tier label.
   */
  name?: string;
  /**
   * One-line subtitle summarising what's in the tier (e.g. "AFUE 80%
   * furnace + standard thermostat"). When absent, the subtitle row is
   * omitted from the card.
   */
  summary?: string;
  total_cents: number;
  quote_type?: 'single' | 'two_column';
  items?: QuoteItem[];
  links?: { quote_proceed?: { href: string } };
  payments_as_low_as_cents?: number;
  jobless_financing?: boolean;
}

export interface Job {
  id: number;
  /**
   * Late-fee settings used to live here on the Vue 3 `Job` interface, but the
   * v2 jobs wire does NOT emit a `settings` field at the job root. Late-fee
   * settings live on the v1 appointment wire (see Appointment.settings above);
   * the late-fee timer now reads `appointment.settings`.
   */
  links: { reserve_s3_photos: { href: string } };
  invoice_id?: number;
  /**
   * The v2 jobs response embeds the invoice when one exists (legacy parity,
   * see research/legacy-magic-link-presentation.md §5). Optional because
   * jobs without an invoice yet (pre-completion) omit the block. Only the
   * fields the magic-link / invoice-card surfaces consume are typed here.
   */
  invoice?: {
    id: number;
    invoice_number?: string | null;
    total_cents: number;
    balance_cents: number;
    paid_at?: string | null;
    /**
     * Due date for an unpaid invoice. The current v2 jobs wire does not always
     * emit this (the appointment-detail InvoiceCard reads it for the "Due {date}"
     * / "Was due {date}" subline); typed optional + nullable so the card can read
     * it without an inline cast (WR-07). Phase 5 wires the real due date here.
     */
    due_at?: string | null;
  };
  /**
   * Quote tiers attached to the job. The detail-surface QuoteCard filters
   * to `quote_status in {pending, denied}` and hides when the filter is
   * empty (CONTEXT D-27). Optional because most jobs never carry quotes.
   */
  quotes?: Quote[];
  /**
   * Customer-visible videos attached to the job. Videos are a job-wire
   * concern — the v1 appointment wire serializes photos + documents but NOT
   * videos, so the appointment-detail media card reads them from here.
   */
  videos?: CustomerVideo[];
  /**
   * Customer-visible photos attached to the job. Sourced from the RICH v2
   * `/v2/jobs/:id` serializer (v2/photos/_photo.json.jbuilder), which — unlike
   * the lean v1 appointment serializer — carries `created_by_name`, so the
   * upload-meta footer can render the "Uploaded by {name}" segment for photos
   * with parity to videos (PODZ-1830 / 07.1-05). The appointment-detail media
   * surface prefers these over `appointment.photos` when present.
   */
  photos?: CustomerPhoto[];
}

export interface TimeSlot {
  id: number;
  starts_at: string;
  ends_at: string;
  // quick-260523-swa Task 8 — backend availability_controller emits these
  // three iso strings per slot (api/app/controllers/v1/availability_controller.rb:100-104).
  // The AvailabilityService adapter now passes them through, and ScheduleView
  // submits them as the appointment's
  // service_time / scheduled_time / arrival_window_start / arrival_window_end.
  // Required-with-defensive-fallback: adapters always populate these (falling
  // back to starts_at / ends_at when backend response omits them).
  scheduled_time: string;
  arrival_window_start: string;
  arrival_window_end: string;
  // quick-260528-od4 (PODZ-1702): backend availability_controller marks slots
  // it has already rejected. Adapters default to false when omitted
  // (transitional safety) so the wizard never receives undefined.
  disabled?: boolean;
}

export interface ServiceCategory {
  id: number;
  name: string;
  subtitle?: string | null;
  duration?: number;
  duration_minutes?: number;
  department_id?: number;
  priority?: number;
  details?: {
    fields?: Array<{ name: string; label: string; options: string[] }>;
  };
  options?: Record<string, unknown>;
}

export interface S3Reservation {
  server_url: string;
  success_url: string;
  aws_params?: Record<string, string>;
}

export interface DocumentReservation {
  server_url: string;
  parameters?: Record<string, string>;
  aws_params?: Record<string, string>;
}

export function extractAwsFields(reservation: {
  parameters?: Record<string, string>;
  aws_params?: Record<string, string>;
}): Record<string, string> {
  return reservation.parameters ?? reservation.aws_params ?? {};
}

const ALLOWED_DOC_MIME_STORE = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;
const DOC_MAX_BYTES_STORE = 20 * 1024 * 1024;

export interface CustomerDocument {
  id: number;
  name: string;
  /**
   * `url` is the embed/preview URL (renders inline in the browser).
   * `downloadUrl` adds a Content-Disposition: attachment header so a click
   * triggers a file download. `thumbUrl` is the auto-generated preview when
   * the backend has processed the document (PDF first-page render).
   */
  url: string;
  downloadUrl?: string;
  thumbUrl?: string;
  customer_visible: boolean;
  createdAt?: string;
  createdByName?: string;
}

export interface CustomerPhoto {
  id: number;
  /**
   * `url` is the full-resolution image. `thumbUrl` is CarrierWave's 200x200
   * `:thumb` version — present when `is_invoice_ready?` is true (post-invoice
   * photos); otherwise falls back to the full url.
   */
  url: string;
  thumbUrl: string;
  customer_visible: boolean;
  createdAt?: string;
  createdByName?: string;
  /**
   * Free-text media caption (PODZ-1830), tech-authored. Absent until backend
   * PODZ-1823 deploys the caption column — render defensively (nothing when
   * undefined).
   */
  caption?: string;
  /**
   * Upload source enum (e.g. `tech_app`, `admin_portal`, `customer_portal`,
   * `widget`). Threaded verbatim from the wire; the upload-meta footer maps it
   * to a display label via the source-label helper (07.1-02) per
   * D-CAP-SOURCE-MAP. Undefined when the wire omits it — footer omits the
   * segment defensively.
   */
  source?: string;
}

export interface AppointmentRow {
  id: number;
  service_date: string;
  status: string;
  service_category?: { name: string };
  address?: { name?: string; address1: string; city: string; state: string };
  total?: string | number;
  /**
   * ISO8601 string from the API datatable's `datetime` field (see
   * api/app/datatables/consumer/appointments_datatable.rb). When present, this
   * is the source of truth for month-grouping in AppointmentsView — the
   * yearless `service_date` ("Fri, May 22") is for display only and is too
   * brittle to parse for arithmetic across year boundaries.
   */
  iso_datetime?: string;
  /**
   * Server-formatted invoice fields from Consumer::AppointmentsDatatable.
   * invoice_balance and invoice_total are formatted strings (e.g. "$245.50")
   * or the sentinel "--" when blank — `display_value` always renders a string.
   * invoice_id is the raw numeric id (null when no invoice exists).
   */
  invoice_id?: number | null;
  invoice_total?: string | null;
  invoice_balance?: string | null;
}

export interface ServiceHistoryResponse {
  data: AppointmentRow[];
  recordsFiltered: number;
}

export interface CreateAppointmentPayload {
  service_category_id: number;
  address_id: number;
  details: Record<string, string>;
  service_time: string;
  scheduled_time: string;
  arrival_window_start: string;
  arrival_window_end: string;
  scheduled_in_web_app: true;
  user_id: number;
}

// eslint-disable-next-line max-lines-per-function -- defineStore setup function aggregates all appointment actions; splitting into separate composables would be a structural refactor outside this task's scope.
export const useAppointmentsStore = defineStore('appointments', () => {
  const toast = useToast();
  const tenantStore = useTenantStore();
  const appointment = ref<Appointment | null>(null);
  const job = ref<Job | null>(null);
  const appointmentComments = ref<AppointmentComment[]>([]);
  const serviceCategories = ref<ServiceCategory[]>([]);
  const availableDates = reactive<Record<string, unknown>>({});
  const fetchedMonths = reactive(new Set<string>());

  // Cached service-history report rows (full set, no pagination) used for
  // nav badges. Distinct from fetchServiceHistory() below, which is the
  // datatable-paginated path used by the appointments list view.
  const report = ref<AppointmentRow[]>([]);
  const reportStatus = ref<ReportStatus>('idle');
  const reportFetchedAt = ref<number | null>(null);
  const reportError = ref<unknown>(null);

  // Date-only YYYY-MM-DD string in the tenant's timezone for "today".
  // Comparing strings sidesteps timezone arithmetic and DST edges.
  function tenantTodayKey(): string {
    const tz = tenantStore.config?.timezone ?? DEFAULT_TIMEZONE;
    // en-CA gives ISO-like YYYY-MM-DD, easy to compare against the same
    // formatting applied to row.iso_datetime.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }

  function rowDateKey(isoDatetime: string | undefined): string | null {
    if (!isoDatetime) return null;
    const tz = tenantStore.config?.timezone ?? DEFAULT_TIMEZONE;
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(isoDatetime));
  }

  // Statuses considered "active" for the nav badge. Mirrors the same set
  // used in AppointmentsView.vue and AppointmentCard.vue for the active /
  // history split. Closed states (completed, cancelled, canceled,
  // quote_denied) are excluded.
  const ACTIVE_STATUSES = new Set([
    'pending',
    'assigned',
    'accepted',
    'running',
    'late',
    'preferred_assignment',
  ]);

  // Actionable count: rows in an active status whose scheduled date is on
  // or before today in tenant TZ. Captures three scenarios in one rule:
  //   - today's appointments still pending / running / late
  //   - overdue appointments that never closed (e.g. running-since-yesterday)
  //   - active appointments missing iso_datetime (rare; included rather than
  //     dropped silently, since active status implies attention needed)
  // Future-scheduled active appointments (next week's pending) are NOT
  // counted: the customer scheduled them, no action is required yet.
  // Hide-when-0 lives at the render site.
  const actionableCount = computed(() => {
    const today = tenantTodayKey();
    return report.value.filter((row) => {
      if (!ACTIVE_STATUSES.has(row.status)) return false;
      const rowKey = rowDateKey(row.iso_datetime);
      // Missing date with active status: include. Comparing YYYY-MM-DD
      // strings is safe because en-CA Intl format produces ISO-ordered keys.
      return rowKey === null || rowKey <= today;
    }).length;
  });

  async function fetchReport(): Promise<void> {
    reportStatus.value = 'loading';
    reportError.value = null;
    try {
      const { appointments } = useServices();
      // per_page=200 covers the long tail. The customer portal historically
      // surfaces under ~50 lifetime appointments. Sort desc by date so today
      // and near-future rows surface early in the array.
      const response = await appointments.fetchServiceHistory({
        page: 0,
        rows: 200,
        sortField: 'date',
        sortOrder: -1,
      });
      report.value = response.data;
      reportStatus.value = 'success';
      reportFetchedAt.value = Date.now();
    } catch (err) {
      reportStatus.value = 'error';
      reportError.value = err;
      // Keep last successful report rows so badges don't flicker to empty.
    }
  }

  function invalidateReport(): void {
    report.value = [];
    reportStatus.value = 'idle';
    reportFetchedAt.value = null;
    reportError.value = null;
  }

  const photoUploadProgress = ref<Map<File, number>>(new Map());
  const documentUploadProgress = ref<Map<File, number>>(new Map());
  const videoUploadProgress = ref<Map<File, number>>(new Map());
  // `*UploadProgress` only emits post-completion, so an in-flight Set is needed for the per-file spinner. Photo Set is keyed by ORIGINAL (not compressed) File.
  const photoUploadInFlight = ref<Set<File>>(new Set());
  const documentUploadInFlight = ref<Set<File>>(new Set());
  const videoUploadInFlight = ref<Set<File>>(new Set());
  // Per-file AbortController registries so the UI can call cancel*Upload(file)
  // to truly tear down the in-flight network request (passed to fetch / axios /
  // XHR via the service-layer `signalFor` callback). Cleared as each file
  // settles. Cancelled-set tracks files the USER aborted so the failure toast
  // distinguishes them from genuine upload errors.
  const photoUploadAborters = ref<Map<File, AbortController>>(new Map());
  const documentUploadAborters = ref<Map<File, AbortController>>(new Map());
  const videoUploadAborters = ref<Map<File, AbortController>>(new Map());
  const photoUploadCancelled = ref<Set<File>>(new Set());
  const documentUploadCancelled = ref<Set<File>>(new Set());
  const videoUploadCancelled = ref<Set<File>>(new Set());

  // PODZ-1701: Gating computed used by the schedule-wizard Confirm button to
  // block submit while any upload is in flight. Reads all three sets so a
  // future photo/document race surfaces the same gate without callsite churn.
  const anyUploadInFlight = computed<boolean>(
    () =>
      photoUploadInFlight.value.size > 0 ||
      documentUploadInFlight.value.size > 0 ||
      videoUploadInFlight.value.size > 0,
  );

  function makeOnProgress(target: { value: Map<File, number> }) {
    return (e: UploadProgressEvent) => {
      const next = new Map(target.value);
      next.set(e.file, e.percent);
      target.value = next;
    };
  }

  function makeInFlightCallbacks(target: { value: Set<File> }): {
    onStart: (file: File) => void;
    removeOnComplete: (file: File) => void;
  } {
    return {
      onStart: (file: File) => {
        const next = new Set(target.value);
        next.add(file);
        target.value = next;
      },
      removeOnComplete: (file: File) => {
        const next = new Set(target.value);
        next.delete(file);
        target.value = next;
      },
    };
  }

  async function scheduleAppointment(
    payload: CreateAppointmentPayload,
    promoCode?: string,
  ): Promise<Appointment> {
    const { appointments } = useServices();
    const data = await appointments.createAppointment(payload, promoCode);
    appointment.value = data;
    // New appointment changes the report. Invalidate so the next badge
    // read triggers a refetch via useReportsFanOut.refresh() (or the caller
    // chooses to refetch explicitly).
    invalidateReport();
    window.gtag?.('event', 'schedule', {
      event_category: 'appointment',
      appointment_id: data.id,
    });
    if (
      typeof window !== 'undefined' &&
      typeof window.pendo !== 'undefined' &&
      typeof (window.pendo as { track?: unknown }).track === 'function'
    ) {
      window.pendo.track('appointment.scheduled', {
        appointment_id: data.id,
        service_category: data.service_category?.name,
      });
    }
    return data;
  }

  // eslint-disable-next-line max-lines-per-function -- compression + in-flight + progress wiring (now drain-on-100) pushed body slightly past 50; splitting would obscure the upload pipeline
  async function uploadPhotos(files: File[], jobId: number): Promise<void> {
    try {
      const { compressIfPhoto } = useFileCompression();
      const compressed = await Promise.all(
        files.map((f) => Promise.resolve(compressIfPhoto(f)).catch(() => f)),
      );
      // Service callbacks key by the compressed File; translate back so consumers can look up by the original reference they hold in draftPhotos.
      const compressedToOriginal = new Map<File, File>();
      compressed.forEach((c, i) => compressedToOriginal.set(c, files[i]));

      photoUploadProgress.value = new Map();
      photoUploadInFlight.value = new Set();
      photoUploadAborters.value = new Map();
      photoUploadCancelled.value = new Set();
      // Per-file aborters are keyed by the ORIGINAL File the parent staged,
      // so cancelPhotoUpload(file) can look up the controller without knowing
      // about the compressed variant the service actually uploads.
      const photoAborters = new Map<File, AbortController>();
      for (const original of files) {
        const controller = new AbortController();
        photoAborters.set(original, controller);
      }
      photoUploadAborters.value = photoAborters;

      const onStart = (compressedFile: File): void => {
        const original = compressedToOriginal.get(compressedFile);
        if (!original) return;
        const next = new Set(photoUploadInFlight.value);
        next.add(original);
        photoUploadInFlight.value = next;
      };
      const baseOnProgress = makeOnProgress(photoUploadProgress);
      // The legacy photo upload now emits XHR byte-level percent during the
      // S3 POST and a final 100% on success. Only drain the in-flight Set on
      // the terminal 100% emit so the per-file percent stays visible from
      // 0% → 99% to the consumer (matches the video pattern in this store).
      const onProgress = (e: UploadProgressEvent): void => {
        baseOnProgress(e);
        if (e.percent < 100) return;
        const original = compressedToOriginal.get(e.file);
        if (!original) return;
        const next = new Set(photoUploadInFlight.value);
        next.delete(original);
        photoUploadInFlight.value = next;
      };
      const signalFor = (compressedFile: File): AbortSignal | undefined => {
        const original = compressedToOriginal.get(compressedFile);
        if (!original) return undefined;
        return photoAborters.get(original)?.signal;
      };

      const { jobs } = useServices();
      const { failedCount } = await jobs.uploadPhotos(
        compressed,
        jobId,
        onProgress,
        onStart,
        signalFor,
      );
      // Suppress the failure toast when every failure is a user cancellation.
      const cancelledCount = photoUploadCancelled.value.size;
      if (failedCount > 0 && failedCount > cancelledCount) {
        toast.add({
          severity: 'warn',
          summary: 'Photos',
          detail: "Some photos didn't upload. View your appointment to retry.",
          life: 8000,
        });
      }
    } catch {
      toast.add({
        severity: 'warn',
        summary: 'Photos',
        detail: "Some photos didn't upload. View your appointment to retry.",
        life: 8000,
      });
    } finally {
      // PODZ-1701 CR-02: drain the in-flight Set on settlement regardless of
      // per-file percent. Failed uploads never reach percent=100 so the
      // per-file drain inside onProgress never fires for them, which would
      // strand them in the Set and stick `anyUploadInFlight` true forever.
      photoUploadInFlight.value = new Set();
    }
  }

  // eslint-disable-next-line max-lines-per-function -- two-phase validation + in-flight wiring inflated body slightly past 50; splitting would obscure flow
  async function uploadDocuments(files: File[], jobId: number): Promise<void> {
    // Reset all four per-file registries up front, before any validation
    // early return, so a prior run's AbortControllers can't leak into this
    // invocation (mirrors the photo path's clean-slate reset).
    documentUploadProgress.value = new Map();
    documentUploadInFlight.value = new Set();
    documentUploadCancelled.value = new Set();
    documentUploadAborters.value = new Map();
    if (files.length === 0) return;
    const invalidFiles = files.filter((f) => {
      const okType =
        (ALLOWED_DOC_MIME_STORE as readonly string[]).includes(f.type) ||
        /\.(pdf|docx?)$/i.test(f.name);
      const okSize = f.size <= DOC_MAX_BYTES_STORE;
      if (!okType || !okSize) {
        toast.add({
          severity: 'warn',
          summary: 'Documents',
          detail: `${f.name} skipped (${!okType ? 'unsupported type' : 'over 20MB'}).`,
          life: 8000,
        });
      }
      return !okType || !okSize;
    });
    const validFiles = files.filter((f) => !invalidFiles.includes(f));
    if (validFiles.length === 0) return;

    try {
      const documentAborters = new Map<File, AbortController>();
      for (const f of validFiles) {
        documentAborters.set(f, new AbortController());
      }
      documentUploadAborters.value = documentAborters;

      const inFlight = makeInFlightCallbacks(documentUploadInFlight);
      const baseOnProgress = makeOnProgress(documentUploadProgress);
      // Same contract as photos: 0% → 99% incremental, final 100% drains.
      const onProgress = (e: UploadProgressEvent): void => {
        baseOnProgress(e);
        if (e.percent >= 100) inFlight.removeOnComplete(e.file);
      };

      const { jobs } = useServices();
      const { failedCount } = await jobs.uploadDocuments(
        validFiles,
        jobId,
        onProgress,
        inFlight.onStart,
        (file) => documentAborters.get(file)?.signal,
      );
      // Suppress the failure toast when every failure is a user cancellation.
      const cancelledCount = documentUploadCancelled.value.size;
      if (failedCount > 0 && failedCount > cancelledCount) {
        toast.add({
          severity: 'warn',
          summary: 'Documents',
          detail: `${failedCount - cancelledCount} document(s) didn't upload.`,
          life: 8000,
        });
      }
    } catch {
      toast.add({
        severity: 'warn',
        summary: 'Documents',
        detail: "Some documents didn't upload. View your appointment to retry.",
        life: 8000,
      });
    } finally {
      // PODZ-1701 CR-02: drain the in-flight Set on settlement (see uploadPhotos).
      documentUploadInFlight.value = new Set();
    }
  }

  async function uploadVideos(
    files: File[],
    jobId: number,
  ): Promise<{ failedCount: number }> {
    // Clear the aborters registry before rebuilding it so a prior run's stale
    // AbortControllers can't survive into this invocation (mirrors the photo
    // path, which resets all four registries up front).
    videoUploadProgress.value = new Map();
    videoUploadInFlight.value = new Set();
    videoUploadCancelled.value = new Set();
    videoUploadAborters.value = new Map();
    try {
      const videoAborters = new Map<File, AbortController>();
      for (const f of files) {
        videoAborters.set(f, new AbortController());
      }
      videoUploadAborters.value = videoAborters;

      const inFlight = makeInFlightCallbacks(videoUploadInFlight);
      const baseOnProgress = makeOnProgress(videoUploadProgress);
      // Videos emit incremental `< 100` percents throughout the upload via
      // XHR `upload.onprogress`. Only drain the in-flight Set on the final
      // `percent === 100` event so the spinner stays up for the whole upload.
      const onProgress = (e: UploadProgressEvent): void => {
        baseOnProgress(e);
        if (e.percent >= 100) inFlight.removeOnComplete(e.file);
      };

      const { jobs } = useServices();
      const { failedCount } = await jobs.uploadVideos(
        files,
        jobId,
        onProgress,
        inFlight.onStart,
        (file) => videoAborters.get(file)?.signal,
      );
      // PODZ-1701: subtract user-cancelled files so the wizard's banner only
      // surfaces genuine upload errors, not user-clicked cancels. The
      // wizard-time failure toast has been removed. The in-page
      // UploadFailureBanner is now the sole wizard-time failure surface.
      const cancelledCount = videoUploadCancelled.value.size;
      return { failedCount: Math.max(0, failedCount - cancelledCount) };
    } catch {
      // PODZ-1701: catch branch no longer emits a toast either. The wizard
      // reads the resolved { failedCount } and decides whether to render the
      // banner; a thrown service error means every file failed.
      return { failedCount: files.length };
    } finally {
      // PODZ-1701 CR-02: drain the in-flight Set on settlement regardless of
      // per-file percent. Failed uploads never reach percent=100 so the
      // per-file drain inside onProgress never fires for them, which would
      // strand them in the Set and stick `anyUploadInFlight` true forever
      // (locking the wizard Confirm button after the banner is dismissed).
      videoUploadInFlight.value = new Set();
    }
  }

  // Per-file cancel functions: abort the in-flight network request, drain the
  // file from the in-flight Set + progress Map so the UI tile disappears, and
  // record it in the cancelled-set so the post-settle failure toast doesn't
  // count user aborts as upload errors.
  function cancelDocumentUpload(file: File): void {
    documentUploadAborters.value.get(file)?.abort();
    const cancelled = new Set(documentUploadCancelled.value);
    cancelled.add(file);
    documentUploadCancelled.value = cancelled;
    const flight = new Set(documentUploadInFlight.value);
    flight.delete(file);
    documentUploadInFlight.value = flight;
    const prog = new Map(documentUploadProgress.value);
    prog.delete(file);
    documentUploadProgress.value = prog;
  }

  function cancelPhotoUpload(file: File): void {
    photoUploadAborters.value.get(file)?.abort();
    const cancelled = new Set(photoUploadCancelled.value);
    cancelled.add(file);
    photoUploadCancelled.value = cancelled;
    const flight = new Set(photoUploadInFlight.value);
    flight.delete(file);
    photoUploadInFlight.value = flight;
    const prog = new Map(photoUploadProgress.value);
    prog.delete(file);
    photoUploadProgress.value = prog;
  }

  function cancelVideoUpload(file: File): void {
    videoUploadAborters.value.get(file)?.abort();
    const cancelled = new Set(videoUploadCancelled.value);
    cancelled.add(file);
    videoUploadCancelled.value = cancelled;
    const flight = new Set(videoUploadInFlight.value);
    flight.delete(file);
    videoUploadInFlight.value = flight;
    const prog = new Map(videoUploadProgress.value);
    prog.delete(file);
    videoUploadProgress.value = prog;
  }

  // quick-260527-njp: full per-account clear on logout / session expiry.
  // Nulls/empties every per-account state ref so a subsequent login as a
  // different account cannot briefly render the prior account's data before a
  // refetch completes. The report surface reuses invalidateReport(); the
  // upload registries are reset to fresh empty Map/Set instances (matching how
  // uploadPhotos / uploadDocuments / uploadVideos reinitialize them). This
  // store does not persist to localStorage, so there is nothing to clear there.
  function reset(): void {
    appointment.value = null;
    job.value = null;
    appointmentComments.value = [];
    serviceCategories.value = [];
    // availableDates is a reactive({}) — reassignment won't propagate; delete
    // each own key in place.
    Object.keys(availableDates).forEach((k) => delete availableDates[k]);
    fetchedMonths.clear();
    invalidateReport();
    photoUploadProgress.value = new Map();
    documentUploadProgress.value = new Map();
    videoUploadProgress.value = new Map();
    photoUploadInFlight.value = new Set();
    documentUploadInFlight.value = new Set();
    videoUploadInFlight.value = new Set();
    photoUploadAborters.value = new Map();
    documentUploadAborters.value = new Map();
    videoUploadAborters.value = new Map();
    photoUploadCancelled.value = new Set();
    documentUploadCancelled.value = new Set();
    videoUploadCancelled.value = new Set();
  }

  async function fetchAppointment(id: number): Promise<Appointment> {
    const { appointments } = useServices();
    const data = await appointments.getAppointment(id);
    appointment.value = data;
    return data;
  }

  async function fetchJob(id: number): Promise<Job> {
    const { jobs } = useServices();
    const data = await jobs.getJob(id);
    job.value = data;
    return data;
  }

  async function fetchAppointmentComments(
    jobId: number,
  ): Promise<AppointmentComment[]> {
    const { appointments } = useServices();
    const data = await appointments.fetchAppointmentComments(jobId);
    appointmentComments.value = data;
    return data;
  }

  async function cancelAppointment(
    id: number,
    payload: { reason: string },
  ): Promise<void> {
    const { appointments } = useServices();
    await appointments.cancelAppointment(id, payload);
    appointment.value = null;
    // Cancelling drops a row from today/upcoming counts and (when pre-billed)
    // frees the matching invoice balance. Invalidate both reports so the
    // next badge read refetches.
    invalidateReport();
    useInvoicesStore().invalidate();
  }

  async function rescheduleAppointment(
    id: number,
    payload: {
      scheduled_time: string;
      arrival_window_start: string;
      arrival_window_end: string;
    },
  ): Promise<void> {
    try {
      const { appointments } = useServices();
      const updated = await appointments.rescheduleAppointment(id, payload);
      appointment.value = updated;
      if (updated.job_id) await fetchJob(updated.job_id);
      // Gap 2 (nav-badge sync): a reschedule can move a row in/out of the
      // today & active-status window, so the badge-driving report is now
      // stale. Refetch FIRE-AND-FORGET so actionableCount self-corrects
      // without blocking reschedule completion. This mirrors the centralized
      // placement of cancelAppointment's cross-store coordination, but
      // REFETCHES (repopulates report[] in place) instead of bare-invalidating
      // — do NOT use invalidateReport() here (would flash the badge to 0).
      // Routed through the store instance so the call goes through the action
      // binding tests spy on; fetchReport swallows its own errors and the
      // .catch keeps the float lint-clean. The catch branch is deliberately
      // untouched: a FAILED reschedule must not refetch.
      useAppointmentsStore()
        .fetchReport()
        .catch(() => undefined);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes('Please choose a different time')
      ) {
        const d = new Date(payload.scheduled_time);
        resetAvailabilityMonth(d.getFullYear(), d.getMonth() + 1);
      }
      throw error;
    }
  }

  async function generateMagicLink(
    id: number,
  ): Promise<{ token: string; expires_at: string }> {
    const { appointments } = useServices();
    return appointments.generateMagicLink(id);
  }

  async function fetchServiceCategories(): Promise<ServiceCategory[]> {
    const { appointments } = useServices();
    const data = await appointments.fetchServiceCategories();
    serviceCategories.value = data;
    return data;
  }

  async function fetchAvailableDates(params: {
    postal_code: string;
    service_category_id: number;
    year: number;
    month: number;
  }): Promise<void> {
    const monthKey = buildMonthKey(params.year, params.month);
    if (fetchedMonths.has(monthKey)) return;
    const category = serviceCategories.value.find(
      (c) => c.id === params.service_category_id,
    );
    const expected_duration = category?.duration ?? 1;
    const { availability } = useServices();
    const data = await availability.fetchAvailableDates({
      ...params,
      expected_duration,
    });
    Object.entries(data).forEach(([k, v]) => {
      availableDates[normalizeAvailabilityKey(k)] = v;
    });
    fetchedMonths.add(monthKey);
  }

  function resetAvailabilityMonth(year: number, month: number): void {
    const monthKey = buildMonthKey(year, month);
    fetchedMonths.delete(monthKey);
    const prefix = `${monthKey}-`;
    Object.keys(availableDates).forEach((k) => {
      if (k.startsWith(prefix)) delete availableDates[k];
    });
  }

  async function fetchTimeSlots(params: {
    postal_code: string;
    service_category_id: number;
    date: string;
  }): Promise<TimeSlot[]> {
    const category = serviceCategories.value.find(
      (c) => c.id === params.service_category_id,
    );
    const expected_duration = category?.duration ?? 1;
    const { availability } = useServices();
    return availability.fetchTimeSlots({ ...params, expected_duration });
  }

  /**
   * Proceed-with-service action for an inline quote tier (CONTEXT D-29).
   *
   * HATEOAS-gated: the request URL is taken verbatim from
   * `quote.links.quote_proceed.href`. Defensive — the caller (QuoteModal)
   * already hides the Proceed button when the link is absent, but we
   * re-check here so a programmatic call cannot bypass the gate.
   *
   * Optimistic UI: on 200, the locally-cached `job.value.quotes[]` entry
   * is flipped to `quote_status === 'accepted'` so the card disappears
   * (the filter drops accepted / expired tiers). On failure a warn toast
   * fires; the local cache is left untouched so the row reappears.
   *
   * Pendo: `customer.quote.proceeded` is tracked with `{ quote_id,
   * appointment_id }` — matches the legacy event name for funnel parity.
   */
  function markQuoteAccepted(quoteId: number): void {
    const currentJob = job.value;
    if (!currentJob?.quotes) return;
    currentJob.quotes = currentJob.quotes.map((q) =>
      q.id === quoteId ? { ...q, quote_status: 'accepted' as const } : q,
    );
  }

  function trackQuoteProceeded(quoteId: number): void {
    const apptId = appointment.value?.id;
    if (
      typeof window === 'undefined' ||
      typeof window.pendo === 'undefined' ||
      typeof (window.pendo as { track?: unknown }).track !== 'function'
    ) {
      return;
    }
    window.pendo.track('customer.quote.proceeded', {
      quote_id: quoteId,
      appointment_id: apptId,
    });
  }

  async function proceedQuote(quoteId: number, notes?: string): Promise<void> {
    const quote = job.value?.quotes?.find((q) => q.id === quoteId);
    const href = quote?.links?.quote_proceed?.href;
    if (!quote || !href) {
      throw new Error(
        'Quote cannot be proceeded — HATEOAS gate is closed (no quote_proceed link).',
      );
    }
    try {
      await apiClient.post(href, { notes: notes ?? '' });
      // Optimistic: flip status so QuoteCard's pending/denied filter drops it.
      markQuoteAccepted(quoteId);
      trackQuoteProceeded(quoteId);
    } catch (err) {
      toast.add({
        severity: 'warn',
        summary: 'Quote',
        detail: "We couldn't submit your request. Please try again.",
        life: 8000,
      });
      throw err;
    }
  }

  async function fetchServiceHistory(lazyParams: {
    page: number;
    rows: number;
    sortField?: string;
    sortOrder?: number;
    filters?: { global?: { value: string } };
    /**
     * Optional AbortSignal forwarded straight to the service layer
     * (and from there into axios). AppointmentsView uses this to cancel
     * in-flight searches when a newer search supersedes the previous one.
     */
    signal?: AbortSignal;
    /**
     * Comma-separated DB enum values from `Appointment.status`
     * (`pending,accepted,running` for the active zone,
     * `completed,cancelled` for the past zone). Forwarded straight to the
     * service layer where it maps to the `status` query param consumed by
     * `AppointmentSearch#status` -> `by_status` (PODZ-1694).
     */
    statusFilter?: string;
  }): Promise<ServiceHistoryResponse> {
    const { appointments } = useServices();
    return appointments.fetchServiceHistory(lazyParams);
  }

  return {
    appointment,
    job,
    appointmentComments,
    serviceCategories,
    availableDates,
    fetchedMonths,
    scheduleAppointment,
    photoUploadProgress,
    photoUploadInFlight,
    documentUploadProgress,
    documentUploadInFlight,
    videoUploadProgress,
    videoUploadInFlight,
    anyUploadInFlight,
    uploadPhotos,
    uploadDocuments,
    uploadVideos,
    cancelPhotoUpload,
    cancelDocumentUpload,
    cancelVideoUpload,
    fetchAppointment,
    fetchJob,
    fetchAppointmentComments,
    cancelAppointment,
    rescheduleAppointment,
    generateMagicLink,
    fetchServiceCategories,
    fetchAvailableDates,
    resetAvailabilityMonth,
    fetchTimeSlots,
    fetchServiceHistory,
    proceedQuote,
    reset,

    // Nav-badge report surface (full report rows, not paginated)
    report,
    reportStatus,
    reportFetchedAt,
    reportError,
    actionableCount,
    fetchReport,
    invalidateReport,
  };
});
